import {
  BarChart3,
  BookOpen,
  Building2,
  FlaskConical,
  GraduationCap,
  Users,
  Wallet,
} from 'lucide-react';

export const MODULE_ORDER = [
  'budget',
  'capital',
  'courses',
  'positions',
  'reporting',
  'research',
  'tuition',
];

const isBudgetOverviewPath = (pathname) => {
  if (pathname === '/budget') return true;
  if (!pathname.startsWith('/budget/')) return false;
  const excludedPrefixes = [
    '/budget/scenarios',
    '/budget/vs-actual',
    '/budget/margins',
    '/budget/revenue-recognition',
    '/budget/book',
  ];
  return !excludedPrefixes.some((prefix) => pathname.startsWith(prefix));
};

const isBudgetPath = (pathname) => {
  if (pathname.startsWith('/planning')) return true;
  if (pathname.startsWith('/budget-model')) return true;
  if (pathname === '/budget') return true;
  if (!pathname.startsWith('/budget/')) return false;
  const excludedPrefixes = [
    '/budget/vs-actual',
    '/budget/margins',
    '/budget/revenue-recognition',
    '/budget/book',
  ];
  return !excludedPrefixes.some((prefix) => pathname.startsWith(prefix));
};

const isCapitalProjectsPath = (pathname) => {
  return (
    pathname === '/capital' ||
    (pathname.startsWith('/capital/') &&
      !pathname.startsWith('/capital/planning') &&
      !pathname.startsWith('/capital/portfolio') &&
      !pathname.startsWith('/capital/budget-actual'))
  );
};

const isResearchSettingsPath = (pathname) => {
  return pathname.startsWith('/research/settings') || pathname.endsWith('/settings');
};

const isResearchPortfolioPath = (pathname) => {
  return pathname.startsWith('/research/portfolio');
};

const isResearchCommitmentsPath = (pathname) => {
  return (
    pathname.startsWith('/research') &&
    !isResearchPortfolioPath(pathname) &&
    !isResearchSettingsPath(pathname)
  );
};

export const MODULE_CONFIG = {
  budget: {
    icon: Wallet,
    path: '/budget',
    isActive: isBudgetPath,
    accent: 'bg-indigo-500',
    iconBg: 'bg-indigo-600',
    pages: [
      {
        label: 'Budget Overview',
        path: '/budget',
        isActive: isBudgetOverviewPath,
      },
      {
        label: 'Planning',
        path: '/planning',
        isActive: (pathname) => pathname.startsWith('/planning'),
      },
      {
        label: 'Budget Model',
        path: '/budget-model',
        isActive: (pathname) => pathname.startsWith('/budget-model'),
      },
      {
        label: 'Budget Scenarios',
        path: '/budget/scenarios',
        isActive: (pathname) => pathname.startsWith('/budget/scenarios'),
      },
    ],
    featureLinks: {
      'Budget Overview': '/budget',
      'Department Planning': '/planning',
      'Budget Model': '/budget-model',
      'Budget Scenarios': '/budget/scenarios',
    },
  },
  capital: {
    icon: Building2,
    path: '/capital/planning',
    isActive: (pathname) => pathname.startsWith('/capital'),
    accent: 'bg-emerald-500',
    iconBg: 'bg-emerald-600',
    pages: [
      {
        label: 'Projects',
        path: '/capital',
        isActive: isCapitalProjectsPath,
      },
      {
        label: 'Planning',
        path: '/capital/planning',
        isActive: (pathname) => pathname.startsWith('/capital/planning'),
      },
      {
        label: 'Portfolio',
        path: '/capital/portfolio',
        isActive: (pathname) => pathname.startsWith('/capital/portfolio'),
      },
      {
        label: 'Budget vs Actual',
        path: '/capital/budget-actual',
        isActive: (pathname) => pathname.startsWith('/capital/budget-actual'),
      },
    ],
    featureLinks: {
      'Capital Project Management': '/capital',
    },
  },
  courses: {
    icon: BookOpen,
    path: '/courses',
    isActive: (pathname) => pathname.startsWith('/courses'),
    accent: 'bg-amber-500',
    iconBg: 'bg-amber-600',
    pages: [
      {
        label: 'Course Planning',
        path: '/courses',
        isActive: (pathname) => pathname.startsWith('/courses'),
      },
    ],
    featureLinks: {
      'Course Scheduling': '/courses',
    },
  },
  positions: {
    icon: Users,
    path: '/positions/budgeting',
    isActive: (pathname) => pathname.startsWith('/positions'),
    accent: 'bg-purple-500',
    iconBg: 'bg-purple-600',
    pages: [
      {
        label: 'Position Budgeting',
        path: '/positions/budgeting',
        isActive: (pathname) => pathname.startsWith('/positions/budgeting'),
      },
    ],
    featureLinks: {
      'Position Budgeting': '/positions/budgeting',
    },
  },
  reporting: {
    icon: BarChart3,
    path: '/superset',
    isActive: (pathname) => pathname.startsWith('/superset'),
    accent: 'bg-sky-500',
    iconBg: 'bg-sky-600',
    pages: [
      {
        label: 'Analytics BI',
        path: '/superset',
        isActive: (pathname) => pathname.startsWith('/superset'),
      },
    ],
    featureLinks: {
      'Analytics BI Integration': '/superset',
    },
  },
  research: {
    icon: FlaskConical,
    path: '/research',
    isActive: (pathname) => pathname.startsWith('/research'),
    accent: 'bg-indigo-500',
    iconBg: 'bg-blue-600',
    pages: [
      {
        label: 'Commitments',
        path: '/research',
        isActive: isResearchCommitmentsPath,
      },
      {
        label: 'Portfolio',
        path: '/research/portfolio',
        isActive: isResearchPortfolioPath,
      },
      {
        label: 'Workload',
        path: '/research/workload',
        isActive: (pathname) => pathname.startsWith('/research/workload'),
        // Manager-only: visible to admin, grant_admin, grant_accountant, research_director
        roles: ['admin', 'superadmin', 'grant_admin', 'grant_accountant', 'research_director', 'finance_director'],
      },
      {
        label: 'Settings',
        path: '/research/settings',
        isActive: isResearchSettingsPath,
      },
    ],
    featureLinks: {
      'Research Commitment Management': '/research',
      'Portfolio Dashboard': '/research/portfolio',
      'Workload Distribution': '/research/workload',
    },
  },
  tuition: {
    icon: GraduationCap,
    path: '/fees/projection',
    isActive: (pathname) =>
      pathname.startsWith('/fees') || pathname.startsWith('/enrollment'),
    accent: 'bg-indigo-500',
    iconBg: 'bg-indigo-600',
    pages: [
      {
        label: 'Fee Projection',
        path: '/fees/projection',
        isActive: (pathname) => pathname.startsWith('/fees/projection'),
      },
      {
        label: 'Cost of Attendance',
        path: '/fees/cost-of-attendance',
        isActive: (pathname) => pathname.startsWith('/fees/cost-of-attendance'),
      },
      {
        label: 'Fee Components',
        path: '/fees/components',
        isActive: (pathname) => pathname.startsWith('/fees/components'),
      },
      {
        label: 'Discount Policies',
        path: '/fees/discounts',
        isActive: (pathname) => pathname.startsWith('/fees/discounts'),
      },
      {
        label: 'Enrollment Funnel',
        path: '/enrollment/funnel',
        isActive: (pathname) => pathname.startsWith('/enrollment/funnel'),
      },
      {
        label: 'Yield Analysis',
        path: '/enrollment/yield',
        isActive: (pathname) => pathname.startsWith('/enrollment/yield'),
      },
    ],
    featureLinks: {
      'Cost of Attendance Editor': '/fees/cost-of-attendance',
      'Fee Components Manager': '/fees/components',
      'Discount Policies': '/fees/discounts',
    },
  },
};

export const getModulePages = (moduleKey) => MODULE_CONFIG[moduleKey]?.pages || [];

export const getDefaultQuickLinks = (moduleKey, limit = 2) => {
  return getModulePages(moduleKey)
    .slice(0, limit)
    .map((page) => page.path);
};

export const normalizeQuickLinks = (moduleKey, quickLinks, limit = 2) => {
  const pages = getModulePages(moduleKey);
  if (!pages.length) return [];
  const allowed = new Set(pages.map((page) => page.path));
  const result = [];
  if (Array.isArray(quickLinks)) {
    quickLinks.forEach((path) => {
      if (!allowed.has(path)) return;
      if (result.includes(path)) return;
      result.push(path);
    });
  }
  return result.slice(0, limit);
};
